/**
 * Where every answer sits on AACS's own voucher.
 *
 * The filled form is drawn in two places — the Worker, which serves it to a teacher, and
 * the school portal, which lets a school print one for its own files. Both draw onto the
 * same supplied file, so both have to agree about where the lines are. They agree by
 * reading this, and nothing here knows anything about PDFs.
 *
 * The page is US Letter, 612 by 792 points, with the origin at the bottom left; the
 * numbers below are distances down from the top, which is how the form reads.
 */
export const VOUCHER_PAGE_HEIGHT = 792;

/**
 * The seven answered blanks above the table, each sitting a couple of points above its
 * own rule the way the session rows do.
 *
 * These were five to nine points too high and two of them too far left, which floated a
 * teacher's name and her school above their lines and put "State (or Region)" over the
 * printed label rather than after it. The rules are in `VOUCHER_FIELD_RULES` and the test
 * beside this file holds every field to its own.
 */
export const VOUCHER_FIELD = {
  name: { fromTop: 212, x: 66 },
  email: { fromTop: 212, x: 348 },
  school: { fromTop: 245, x: 70 },
  city: { fromTop: 245, x: 382 },
  state: { fromTop: 245, x: 545 },
  conventionDates: { fromTop: 278, x: 194 },
  region: { fromTop: 299, x: 112 },
} as const;

/**
 * Where each of those blanks is actually ruled on the supplied file: the rule's distance
 * from the top, and where it starts and ends across the page. Measured the same way as
 * the session rules — rendered at 72 dpi, one pixel to the point.
 *
 * Two lines share a rule at 214 and three share one at 247, which is why they read as one
 * row on the form and are listed separately here.
 */
export const VOUCHER_FIELD_RULES = {
  name: { fromTop: 214, startsAt: 63, endsAt: 305 },
  email: { fromTop: 214, startsAt: 345, endsAt: 574 },
  school: { fromTop: 247, startsAt: 69, endsAt: 358 },
  city: { fromTop: 247, startsAt: 379, endsAt: 515 },
  state: { fromTop: 247, startsAt: 542, endsAt: 574 },
  conventionDates: { fromTop: 280, startsAt: 192, endsAt: 574 },
  region: { fromTop: 301, startsAt: 109, endsAt: 574 },
} as const;

/**
 * The ten rules in the session table, and the two columns they carry.
 *
 * A row's number is where the text sits, and text sits **on** a rule the way a person
 * writing by hand puts it there: baseline a couple of points above the line, not on top
 * of it. `firstRowFromTop` was 354 and `rowHeight` 19.3, which drew every title two
 * points below the rule above it — so the rule crossed the letters at x-height and every
 * entry came out looking struck through, on a document two people sign and mail to AACS.
 *
 * These are measured off the supplied file rather than guessed. Rendering it at 72 dpi,
 * where one pixel is one point, puts the ten writing rules at the distances in
 * `VOUCHER_RULES_FROM_TOP`. The test beside this file checks every row lands just above
 * its own rule, so if AACS reissues the form the test says so rather than the vouchers
 * quietly going wrong again.
 */
export const VOUCHER_TABLE = {
  firstRowFromTop: 369,
  rowHeight: 18.9,
  rows: 10,
  titleX: 42,
  categoryX: 385,
  totalFromTop: 556,
  totalX: 48,
} as const;

/** How wide a session title may be drawn before it has to be shortened. */
export const VOUCHER_TITLE_MAX_WIDTH = 330;

/**
 * The ten writing rules, in points from the top, measured off the supplied
 * `aacs-attendance-voucher.pdf`. Redo the measurement and this list if AACS reissues
 * the form.
 */
export const VOUCHER_RULES_FROM_TOP = [371, 390, 409, 428, 446, 465, 484, 503, 522, 541] as const;

/** Where row `index` writes, in points from the top of the page. */
export function voucherRowFromTop(index: number): number {
  return VOUCHER_TABLE.firstRowFromTop + index * VOUCHER_TABLE.rowHeight;
}

/**
 * A filename a teacher can find again in a downloads folder a year later.
 *
 * Somebody with no name to put in it gets the part dropped rather than repeated, because
 * the word "voucher" is already there.
 */
export function voucherFileName(name: string, conventionDates: string): string {
  const person = name.trim().replace(/\s+/gu, "-").replace(/[^A-Za-z0-9-]/gu, "");
  const year = conventionDates.match(/\d{4}/u)?.[0] ?? "";
  return ["AACS-attendance-voucher", person, year].filter(Boolean).join("-") + ".pdf";
}

/** The form asks for "month, days, year", so it gets one month and one year. */
export function formatConventionDates(startDate: string, endDate: string): string {
  const start = new Date(`${startDate}T12:00:00Z`);
  const end = new Date(`${endDate}T12:00:00Z`);
  const month = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(start);
  const endMonth = new Intl.DateTimeFormat("en-US", { month: "long", timeZone: "UTC" }).format(end);
  const tail = month === endMonth
    ? `${start.getUTCDate()}–${end.getUTCDate()}`
    : `${start.getUTCDate()} – ${endMonth} ${end.getUTCDate()}`;
  return `${month} ${tail}, ${end.getUTCFullYear()}`;
}
